import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Users, DollarSign, ChevronDown, Plus, X, Mail, ArrowLeft } from 'lucide-react';
import { LogoHorizontal } from '../components/Logo';
import { PrimaryButton, TextLink } from '../components/Button';
import { FormHeader } from '../components/Card';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function CreateFamily() {
    const navigate = useNavigate();
    const { config, refreshMe } = useAuth();
    const fileRef = useRef(null);

    const [name, setName] = useState('');
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [currency, setCurrency] = useState('GTQ');
    const [invites, setInvites] = useState([]);
    const [newInvite, setNewInvite] = useState({ name: '', email: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [created, setCreated] = useState(null);

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setAvatarPreview(reader.result);
            setAvatarUrl(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const addInvite = () => {
        const email = newInvite.email.trim().toLowerCase();
        if (!email || !email.includes('@')) return;
        if (invites.find((i) => i.email === email)) return;
        setInvites((arr) => [...arr, { name: newInvite.name.trim(), email }]);
        setNewInvite({ name: '', email: '' });
    };

    const removeInvite = (email) => setInvites((arr) => arr.filter((i) => i.email !== email));

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (name.trim().length < 2) return setError('Nombre de familia debe tener al menos 2 caracteres');
        if (!currency) return setError('Selecciona una moneda');
        setLoading(true);
        try {
            const { data } = await api.post('/family/create', {
                name: name.trim(),
                avatar_url: avatarUrl || undefined,
                currency,
                invite_emails: invites.map((i) => i.email),
            });
            await refreshMe();
            setCreated(data.family);
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    if (created) {
        return (
            <div className="min-h-screen bg-surface app-shell flex flex-col py-8 px-6">
                <div className="flex justify-center mb-6"><LogoHorizontal size="lg" /></div>
                <div className="bg-white rounded-[24px] shadow-card p-6 fade-up text-center" data-testid="family-created-card">
                    <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                        <Users className="w-12 h-12 text-emerald-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-brand-navy">¡Tu familia está lista!</h2>
                    <p className="text-gray-500 mt-2">Comparte este código para invitar miembros:</p>
                    <div className="my-5 p-4 bg-brand/5 border-2 border-dashed border-brand/30 rounded-2xl">
                        <div className="text-xs text-brand-navy font-semibold uppercase tracking-wide mb-1">Código de familia</div>
                        <div className="text-2xl font-bold tracking-wider text-brand" data-testid="created-family-code">{created.code}</div>
                    </div>
                    <PrimaryButton onClick={() => navigate('/home', { replace: true })} testId="family-created-continue-btn">Ir al inicio</PrimaryButton>
                </div>
            </div>
        );
    }

    const currencies = config?.currencies || [
        { code: 'GTQ', name: 'Quetzal (Guatemala)' },
        { code: 'USD', name: 'Dólar' },
    ];

    return (
        <div className="min-h-screen bg-surface app-shell flex flex-col py-8 px-6">
            <div className="flex items-center justify-between mb-6">
                <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="create-family-back">
                    <ArrowLeft className="w-5 h-5 text-brand-navy" />
                </button>
                <LogoHorizontal size="md" />
                <div className="w-10" />
            </div>

            <div className="bg-white rounded-[24px] shadow-card p-6 fade-up">
                <FormHeader title="Crear tu Familia" subtitle="Configura tu hogar digital y empieza a organizarte." />

                <form onSubmit={submit} className="space-y-4">
                    {/* Avatar */}
                    <div className="flex flex-col items-center">
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="w-24 h-24 bg-brand/10 hover:bg-brand/20 rounded-full flex items-center justify-center mb-2 overflow-hidden transition"
                            data-testid="create-family-avatar-btn"
                        >
                            {avatarPreview ? (
                                <img src={avatarPreview} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="relative">
                                    <Camera className="w-9 h-9 text-brand" />
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand rounded-full text-white text-[10px] flex items-center justify-center font-bold">+</span>
                                </div>
                            )}
                        </button>
                        <span className="text-sm text-gray-500 font-medium">Añade un avatar para la familia</span>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="create-family-avatar-input" />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-1.5">Nombre de la familia</label>
                        <div className="relative">
                            <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Ej. Familia García"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full h-14 bg-white border border-gray-200 rounded-2xl pl-12 pr-4 text-base focus:border-brand focus:ring-2 focus:ring-brand/20"
                                data-testid="create-family-name-input"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-1.5">Moneda</label>
                        <div className="relative">
                            <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <select
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="w-full h-14 bg-white border border-gray-200 rounded-2xl pl-12 pr-12 text-base appearance-none focus:border-brand focus:ring-2 focus:ring-brand/20"
                                data-testid="create-family-currency-select"
                            >
                                {currencies.map((c) => (
                                    <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                        </div>
                    </div>

                    {/* Invitations */}
                    <div className="pt-3 border-t border-gray-100">
                        <h3 className="text-sm font-bold text-brand-navy mb-1">Invitar miembros</h3>
                        <p className="text-xs text-gray-500 mb-3">Opcional — puedes invitar después también</p>

                        {invites.length > 0 && (
                            <div className="space-y-2 mb-3">
                                {invites.map((i) => (
                                    <div key={i.email} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl" data-testid={`invite-row-${i.email}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full bg-brand/15 text-brand font-bold text-sm flex items-center justify-center">
                                                {(i.name || i.email).slice(0, 1).toUpperCase()}
                                            </div>
                                            <div>
                                                {i.name && <div className="text-sm font-semibold text-gray-800">{i.name}</div>}
                                                <div className="text-xs text-gray-500">{i.email}</div>
                                            </div>
                                        </div>
                                        <button type="button" onClick={() => removeInvite(i.email)} className="text-gray-400 hover:text-red-500 p-1" data-testid={`remove-invite-${i.email}`}>
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="space-y-2">
                            <input
                                type="text"
                                placeholder="Nombre (opcional)"
                                value={newInvite.name}
                                onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                                className="w-full h-12 bg-white border border-gray-200 rounded-2xl px-4 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20"
                                data-testid="invite-name-input"
                            />
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="email"
                                        placeholder="correo@ejemplo.com"
                                        value={newInvite.email}
                                        onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                                        className="w-full h-12 bg-white border border-gray-200 rounded-2xl pl-11 pr-4 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20"
                                        data-testid="invite-email-input"
                                    />
                                </div>
                                <button type="button" onClick={addInvite} className="h-12 px-4 bg-brand text-white rounded-2xl font-semibold hover:bg-brand-dark active:scale-95 transition flex items-center gap-1" data-testid="invite-add-btn">
                                    <Plus className="w-4 h-4" /> Agregar
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && <div className="text-red-500 text-sm font-medium" data-testid="create-family-error">{error}</div>}

                    <PrimaryButton type="submit" loading={loading} testId="create-family-submit-btn">Crear familia</PrimaryButton>
                </form>
            </div>
        </div>
    );
}
